import type { ReactNode } from 'react'
import { formatNumber } from '@/lib/format'

export interface ShareSegment {
  readonly label: string
  readonly value: number
  /** CSS-Variable aus der Palette, etwa "var(--viz-series-1)". */
  readonly color: string
}

interface StackedShareProps {
  readonly segments: readonly ShareSegment[]
}

/**
 * Anteile an einem Ganzen als einzelner gestapelter Balken.
 *
 * Bewusst kein Tortendiagramm: Winkel lassen sich schlechter vergleichen
 * als Längen, und bei drei Werten braucht es dafür keine eigene Fläche.
 *
 * Zwischen den Segmenten liegt eine 2px-Lücke in der Hintergrundfarbe.
 * Ohne sie verschwimmen benachbarte Flächen ineinander, besonders für
 * Menschen mit Farbsehschwäche.
 */
export function StackedShare({ segments }: StackedShareProps): ReactNode {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0)

  if (total === 0) {
    return <p className="py-4 text-sm text-slate-500 dark:text-slate-400">Keine Daten</p>
  }

  return (
    <div>
      <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-sm">
        {segments.map((segment) => {
          if (segment.value === 0) return null
          return (
            <div
              key={segment.label}
              style={{
                width: `${String((segment.value / total) * 100)}%`,
                backgroundColor: segment.color,
              }}
              className="first:rounded-l-sm last:rounded-r-sm"
            />
          )
        })}
      </div>

      {/* Legende mit Zahl: Die Farbe allein trägt die Bedeutung nie. */}
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {segments.map((segment) => (
          <li key={segment.label} className="flex items-center gap-2 text-sm">
            <span
              aria-hidden
              className="size-2.5 shrink-0 rounded-xs"
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-slate-600 dark:text-slate-300">{segment.label}</span>
            <span className="tabular-nums font-medium text-slate-900 dark:text-slate-100">
              {formatNumber(segment.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
